import React, { useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faLock,
	faCalendarDay,
	faClock,
	faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';

import {
	createWorkshopCheckout,
	groupSeatsRemaining,
	workshopSeatsRemaining,
	ageRangeError,
	formatSessionOption,
} from '../../utils/workshopService';
import { stashPendingPurchase } from '../../utils/metaPixel';
import { isValidEmail, friendlyServerError } from '../../utils/formValidation';
import WaitlistForm from './WaitlistForm';
import './SummerCheckout.css';

const initialForm = {
	parentName: '',
	parentEmail: '',
	parentPhone: '',
	studentName: '',
	studentAge: '',
	emergencyName: '',
	emergencyPhone: '',
	priorExperience: '',
	medicalNotes: '',
	interestedInFallLessons: false,
	mediaConsent: '',
	acknowledgedRefundPolicy: false,
};

// Recording/media-release choices, shown only for workshops with a recorded
// deliverable (workshop.requiresMediaConsent). Default delivery is private to
// the family — public sharing is strictly opt-in.
const MEDIA_CONSENT_OPTIONS = [
	{
		value: 'private',
		label: 'Yes — you may record my child, for private delivery to our family only',
	},
	{
		value: 'public',
		label:
			'Yes — you may record my child, and may also share clips publicly (CYV website/social)',
	},
	{ value: 'none', label: 'No — please do not record my child' },
];

// Themed order-summary bar per workshop (matches the Surfaces 1–2 accents).
const GRAD = {
	songwriting: 'var(--grad-custom)',
	allstate: 'var(--grad-summer)',
	acting: 'var(--grad-lessons)',
	mtaudition: 'var(--grad-littles)',
};

// "9:00 AM" → "9am"; "12:30 PM" → "12:30pm".
function shortTime(t) {
	if (!t) return '';
	const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(t).trim());
	if (!m) return t;
	const [, h, mm, ap] = m;
	return mm === '00' ? `${h}${ap.toLowerCase()}` : `${h}:${mm}${ap.toLowerCase()}`;
}
const timeSpan = (g) =>
	g?.startTime && g?.endTime ? `${shortTime(g.startTime)}–${shortTime(g.endTime)}` : 'Time TBD';

const RegistrationForm = ({ workshop }) => {
	const groups = useMemo(() => workshop.groups || [], [workshop]);
	const requiresGroupSelection = !!workshop.requiresGroupSelection;
	const requiresMediaConsent = !!workshop.requiresMediaConsent;

	// Default group: the single group when there's only one.
	const [groupId, setGroupId] = useState(
		requiresGroupSelection ? '' : groups[0]?.id || ''
	);
	const [form, setForm] = useState(initialForm);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	const totalSeatsLeft = workshopSeatsRemaining(workshop);
	const isFull = workshop.status !== 'draft' && totalSeatsLeft <= 0;
	const isClosed = workshop.status === 'closed';

	const update = (field) => (e) => {
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');

		if (!form.parentName.trim()) {
			setError('Please add a parent or guardian name so we know who to reach.');
			return;
		}
		if (!isValidEmail(form.parentEmail)) {
			setError('Please add an email so we can send your confirmation.');
			return;
		}
		if (!form.parentPhone.trim()) {
			setError('Please add a phone number so we can reach you.');
			return;
		}
		if (!form.studentName.trim()) {
			setError('Please add the student’s name.');
			return;
		}
		if (!`${form.studentAge}`.trim()) {
			setError('Please add the student’s age.');
			return;
		}
		if (requiresGroupSelection && !groupId) {
			setError('Please choose a session.');
			return;
		}
		const ageErr = ageRangeError(groups.find((g) => g.id === groupId), form.studentAge);
		if (ageErr) {
			setError(ageErr);
			return;
		}
		if (!form.emergencyName.trim()) {
			setError('Please add an emergency contact name.');
			return;
		}
		if (!form.emergencyPhone.trim()) {
			setError('Please add an emergency contact phone number.');
			return;
		}
		if (requiresMediaConsent && !form.mediaConsent) {
			setError('Please choose a recording preference to continue.');
			return;
		}
		if (!form.acknowledgedRefundPolicy) {
			setError('Please acknowledge the refund policy to continue.');
			return;
		}

		setSubmitting(true);
		try {
			const { url } = await createWorkshopCheckout({
				workshopId: workshop.slug || workshop.id,
				groupId,
				parent: {
					name: form.parentName.trim(),
					email: form.parentEmail.trim(),
					phone: form.parentPhone.trim(),
				},
				student: {
					name: form.studentName.trim(),
					age: form.studentAge.trim(),
				},
				emergencyContact: {
					name: form.emergencyName.trim(),
					phone: form.emergencyPhone.trim(),
				},
				options: {
					priorExperience: form.priorExperience.trim() || null,
					medicalNotes: form.medicalNotes.trim() || null,
					interestedInFallLessons: form.interestedInFallLessons,
					mediaConsent: requiresMediaConsent ? form.mediaConsent || null : null,
				},
				acknowledgedRefundPolicy: form.acknowledgedRefundPolicy,
			});
			if (url) {
				// Remember the amount for the Meta Pixel Purchase event on the
				// success surface (/summer/:id?status=success carries no amount).
				// workshop.price is the flat per-student price the server charges.
				stashPendingPurchase({ value: workshop.price, currency: 'USD' });
				window.location.assign(url); // hand off to Stripe Checkout (hosted)
			} else {
				setError('Something went wrong starting checkout. Please try again.');
				setSubmitting(false);
			}
		} catch (err) {
			console.error('Checkout failed:', err);
			setError(friendlyServerError(err, 'We could not start checkout. Please try again.'));
			setSubmitting(false);
		}
	};

	const grad = GRAD[workshop.heroTheme] || GRAD.acting;

	// Closed or full → capture a lead via the waitlist instead of a live
	// checkout (Review 3 #36). A closed offering never reaches the 12-field form.
	if (isClosed || isFull) {
		return (
			<div className='cyv co' style={{ '--co-grad': grad }}>
				<div className='co-head'>
					<h1>
						{isClosed ? 'Enrollment closed' : 'Join the waitlist'} · {workshop.title}
					</h1>
					<p>
						{isClosed
							? 'Enrollment for this workshop is closed. Leave your details and we’ll reach out if a spot opens up or we run it again.'
							: 'This workshop is full — leave your details and we’ll reach out if a spot opens.'}
					</p>
				</div>
				<WaitlistForm workshop={workshop} />
			</div>
		);
	}

	// ---- Live order-summary values ----
	const selectedGroup = groups.find((g) => g.id === groupId);
	const multiDay = workshop.startDate && workshop.endDate && workshop.startDate !== workshop.endDate;
	const datePretty = (workshop.dateLabel || '')
		.replace(/,\s*\d{4}/, '')
		.replace(/\s*\(([^)]+)\)/, ' · $1');
	const summaryTime = requiresGroupSelection
		? selectedGroup
			? `${selectedGroup.label} · ${timeSpan(selectedGroup)}`
			: 'Select a session below'
		: `${timeSpan(groups[0])}${multiDay ? ' daily' : ''}`;

	return (
		<div className='cyv co' style={{ '--co-grad': grad }}>
			<div className='co-head'>
				<h1>Register · {workshop.title}</h1>
				<p>A couple of details and you're in. It takes about two minutes.</p>
			</div>

			<form className='co-body' onSubmit={handleSubmit} noValidate>
				<main className='co-main'>
					{/* Step 1 — Student & family */}
					<section className='co-step'>
						<div className='co-step__h'>
							<span className='co-step__n'>1</span>
							<h2 className='co-step__t'>Student &amp; family</h2>
						</div>

						<div className='co-grid'>
							<div className='co-field'>
								<label htmlFor='co-parent-name'>
									Parent / guardian <span className='req'>*</span>
								</label>
								<input
									id='co-parent-name'
									className='co-input'
									type='text'
									required
									autoComplete='name'
									value={form.parentName}
									onChange={update('parentName')}
								/>
							</div>
							<div className='co-field'>
								<label htmlFor='co-parent-email'>
									Email <span className='req'>*</span>
								</label>
								<input
									id='co-parent-email'
									className='co-input'
									type='email'
									required
									autoComplete='email'
									value={form.parentEmail}
									onChange={update('parentEmail')}
								/>
							</div>
							<div className='co-field'>
								<label htmlFor='co-parent-phone'>
									Phone <span className='req'>*</span>
								</label>
								<input
									id='co-parent-phone'
									className='co-input'
									type='tel'
									required
									autoComplete='tel'
									value={form.parentPhone}
									onChange={update('parentPhone')}
								/>
							</div>
							<div className='co-field'>
								<label htmlFor='co-student-name'>
									Student name <span className='req'>*</span>
								</label>
								<input
									id='co-student-name'
									className='co-input'
									type='text'
									required
									value={form.studentName}
									onChange={update('studentName')}
								/>
							</div>
							<div className='co-field'>
								<label htmlFor='co-student-age'>
									Student age <span className='req'>*</span>
								</label>
								<input
									id='co-student-age'
									className='co-input'
									type='number'
									min='3'
									max='19'
									required
									value={form.studentAge}
									onChange={update('studentAge')}
								/>
							</div>

							{/* Session/group select (hidden unless the workshop has groups) */}
							{requiresGroupSelection && (
								<div className='co-field full'>
									<label htmlFor='co-group'>
										Choose a session <span className='req'>*</span>
									</label>
									<select
										id='co-group'
										className='co-input'
										required
										value={groupId}
										onChange={(e) => setGroupId(e.target.value)}>
										<option value=''>Select a session…</option>
										{groups.map((g) => {
											const groupFull =
												workshop.status !== 'draft' && groupSeatsRemaining(g) <= 0;
											return (
												<option key={g.id} value={g.id} disabled={groupFull}>
													{formatSessionOption(g)}
													{groupFull ? ' — Full' : ''}
												</option>
											);
										})}
									</select>
								</div>
							)}

							<div className='co-field'>
								<label htmlFor='co-emerg-name'>
									Emergency contact name <span className='req'>*</span>
								</label>
								<input
									id='co-emerg-name'
									className='co-input'
									type='text'
									required
									value={form.emergencyName}
									onChange={update('emergencyName')}
								/>
							</div>
							<div className='co-field'>
								<label htmlFor='co-emerg-phone'>
									Emergency contact phone <span className='req'>*</span>
								</label>
								<input
									id='co-emerg-phone'
									className='co-input'
									type='tel'
									required
									value={form.emergencyPhone}
									onChange={update('emergencyPhone')}
								/>
							</div>

							<div className='co-field full'>
								<label htmlFor='co-experience'>
									Prior music/theatre experience{' '}
									<span className='optional'>(optional)</span>
								</label>
								<textarea
									id='co-experience'
									className='co-input'
									rows={2}
									value={form.priorExperience}
									onChange={update('priorExperience')}
								/>
							</div>
							<div className='co-field full'>
								<label htmlFor='co-medical'>
									Medical notes or allergies{' '}
									<span className='optional'>(optional)</span>
								</label>
								<textarea
									id='co-medical'
									className='co-input'
									rows={2}
									value={form.medicalNotes}
									onChange={update('medicalNotes')}
								/>
							</div>
						</div>
					</section>

					{/* Step 2 — Permissions */}
					<section className='co-step'>
						<div className='co-step__h'>
							<span className='co-step__n'>2</span>
							<h2 className='co-step__t'>Permissions</h2>
						</div>

						{requiresMediaConsent && (
							<div className='co-field full' style={{ marginBottom: '4px' }}>
								<p className='co-choices__label'>
									Recording preference <span className='req'>*</span>
								</p>
								<p className='co-help'>
									On the final day, artists have the option to make an informal
									recording of their song. Recordings are delivered privately to
									your family unless you choose otherwise.
								</p>
								{MEDIA_CONSENT_OPTIONS.map((opt) => (
									<label className='co-consent' key={opt.value}>
										<input
											type='radio'
											name='mediaConsent'
											value={opt.value}
											checked={form.mediaConsent === opt.value}
											onChange={update('mediaConsent')}
										/>
										<span>{opt.label}</span>
									</label>
								))}
							</div>
						)}

						<label className='co-consent'>
							<input
								type='checkbox'
								checked={form.interestedInFallLessons}
								onChange={update('interestedInFallLessons')}
							/>
							<span>
								I'd like to hear about fall lessons at Color Your Voice.{' '}
								<span className='muted'>Optional.</span>
							</span>
						</label>

						<label className='co-consent'>
							<input
								type='checkbox'
								required
								checked={form.acknowledgedRefundPolicy}
								onChange={update('acknowledgedRefundPolicy')}
							/>
							<span>
								<strong>
									Registration &amp; refund policy <span className='req'>*</span>
								</strong>{' '}
								I understand the refund policy: full refund if I cancel 14+ days
								before the start date, or within 48 hours of registering —
								whichever is later.{' '}
								<a href='/terms' target='_blank' rel='noreferrer'>
									Read our full policies &amp; terms →
								</a>
							</span>
						</label>
					</section>

					{/* Step 3 — Payment (REAL Stripe hosted checkout; no card fields) */}
					<section className='co-step'>
						<div className='co-step__h'>
							<span className='co-step__n'>3</span>
							<h2 className='co-step__t'>Payment</h2>
						</div>

						{error && (
							<Alert variant='danger' className='mb-3'>
								{error}
							</Alert>
						)}

						<button
							type='submit'
							className='btn-cyv-teal co-pay'
							disabled={submitting}>
							{submitting ? (
								<>
									<Spinner animation='border' size='sm' className='me-2' />
									Starting checkout…
								</>
							) : (
								`Continue to secure checkout — ${workshop.priceLabel}`
							)}
						</button>

						<p className='co-stripe'>
							<FontAwesomeIcon icon={faLock} /> Payments are processed securely by
							Stripe — you'll enter card details on Stripe's secure page, and
							we'll email your confirmation once you're done.
						</p>
						<p className='co-refund'>
							Full refund 14+ days before the workshop starts, or within 48 hours
							of registering — whichever is later. Within that window, tuition is
							non-refundable. Full refund if Color Your Voice cancels.
						</p>
						<p className='co-faqhint'>
							Questions before you register? <a href='/faq'>See our FAQ</a>
						</p>
					</section>
				</main>

				{/* Order summary (live workshop data) */}
				<aside className='co-aside'>
					<div className='co-sum'>
						<div className='co-sum__bar' />
						<div className='co-sum__in'>
							<p className='co-sum__title'>Your registration</p>
							<h3 className='co-sum__name'>{workshop.title}</h3>
							<ul className='co-sum__list'>
								<li>
									<FontAwesomeIcon icon={faCalendarDay} />
									<span>{datePretty}</span>
								</li>
								<li>
									<FontAwesomeIcon icon={faClock} />
									<span>{summaryTime}</span>
								</li>
								<li>
									<FontAwesomeIcon icon={faLocationDot} />
									<span>{workshop.location?.name}, Burnsville</span>
								</li>
							</ul>
							<div className='co-line'>
								<span>Tuition · 1 student</span>
								<span>{workshop.priceLabel}</span>
							</div>
							<div className='co-total'>
								<span className='k'>Total</span>
								<span className='v'>{workshop.priceLabel}</span>
							</div>
							<div className='co-secure'>
								<FontAwesomeIcon icon={faLock} /> Secure checkout
							</div>
						</div>
					</div>
				</aside>
			</form>
		</div>
	);
};

export default RegistrationForm;
